import Badge from "@/v2-ui/badge"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { DotText } from "@/v2-backoffice-client/components/dot"
import {
  MATCH_KIND_LABEL,
  MATCH_SOURCE_BACKOFFICE,
  getMatchBadgeVariant,
  getMatchDotVariant,
} from "@/v2-backoffice-client/crm/crm.constants"
import type { CustomerCreateMatchesQuery } from "@/v2-backoffice-client/customer/create/__types__/customer.create.matches.query"

export type CustomerMatchCandidate =
  CustomerCreateMatchesQuery["findCustomerMatches"]["Candidates"][number]

export type CustomerMatchListProps = {
  candidates: CustomerMatchCandidate[]
  selectedExternalCompanyId?: string
  onSelectCompany(candidate: CustomerMatchCandidate): void
  onOpenCustomer(candidate: CustomerMatchCandidate): void
}

/**
 * The create-customer duplicate check's hit list (dropdown content): one
 * ranked list of divided rows on an elevated surface — no nested cards —
 * each row saying where the hit lives as a colored status dot (red/amber =
 * Backoffice, green/blue = HubSpot; same palette as every other dot in the
 * flow) and offering the one action that fits: open the existing customer,
 * or use the HubSpot company so the new customer is created linked to it.
 */
function CustomerMatchList(props: CustomerMatchListProps) {
  const {
    candidates,
    selectedExternalCompanyId,
    onSelectCompany,
    onOpenCustomer,
  } = props

  return (
    <div className="crm-match-list flex max-h-80 flex-col overflow-auto">
      <ul className="divide-y divide-border/60">
        {candidates.map((candidate) => {
          const isBackoffice = candidate.Source === MATCH_SOURCE_BACKOFFICE
          const isSelected =
            !isBackoffice &&
            !!candidate.ExternalCompanyId &&
            candidate.ExternalCompanyId === selectedExternalCompanyId
          const details = [
            candidate.OrganizationNumber &&
              `Org.nr ${candidate.OrganizationNumber}`,
            candidate.Domain,
            candidate.City,
            candidate.OwnerName && `Owner ${candidate.OwnerName}`,
            isBackoffice &&
              candidate.UserCount != null &&
              `${candidate.UserCount} user${candidate.UserCount === 1 ? "" : "s"}`,
          ]
            .filter(Boolean)
            .join(" · ")

          return (
            <li
              key={`${candidate.Source}:${candidate.CustomerId ?? candidate.ExternalCompanyId}`}
              className={cn(
                "flex items-center justify-between gap-3 px-3 py-2 transition-colors hover:bg-accent/40",
                isSelected && "bg-primary/10 hover:bg-primary/15",
              )}
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {candidate.Name}
                  </span>
                  <Badge
                    size="xs"
                    variant={getMatchBadgeVariant(
                      candidate.Source,
                      candidate.MatchKind,
                    )}
                  >
                    {MATCH_KIND_LABEL[candidate.MatchKind]}
                  </Badge>
                </div>
                {details && (
                  <span className="truncate text-xs text-muted-foreground">
                    {details}
                  </span>
                )}
                <DotText
                  variant={getMatchDotVariant(
                    candidate.Source,
                    candidate.MatchKind,
                  )}
                  className="text-xs"
                >
                  {isBackoffice
                    ? `Already a customer in Backoffice${candidate.ExternalCompanyId ? " · linked to HubSpot" : ""}`
                    : "In HubSpot — not in Backoffice yet"}
                </DotText>
              </div>

              {isBackoffice ? (
                <Button
                  type="button"
                  size="tiny"
                  variant="outline"
                  iconSrc="arrow-up-right"
                  iconPosition="right"
                  onClick={() => {
                    onOpenCustomer(candidate)
                  }}
                >
                  Open
                </Button>
              ) : (
                <Button
                  type="button"
                  size="tiny"
                  variant={isSelected ? "primary" : "outline"}
                  iconSrc={isSelected ? "check" : "link"}
                  onClick={() => {
                    onSelectCompany(candidate)
                  }}
                >
                  {isSelected ? "Selected" : "Use this company"}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default CustomerMatchList
